import { Search } from "react-bootstrap-icons";
import Fuse from "fuse.js";
import { StoreCategoryModel, StoreProductModel } from "../../../models";
import { useNavigate } from "react-router-dom";
import { useDebounce, useStore } from "../../../hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import "./SearchBar.css";
import { toAbsoluteUrl } from "../../../helpers";

const DEFAULT_IMAGE = toAbsoluteUrl("images/av5c.png");

export const SearchBar = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery.trim(), 300);

  const { storeProducts = [], storeCategories = [] } = useStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const categoryMap = useMemo(
    () =>
      new Map(
        storeCategories.map((category: StoreCategoryModel) => [
          category.store_category_id,
          category.category_name,
        ]),
      ),
    [storeCategories],
  );

  const fuse = useMemo(
    () =>
      new Fuse(storeProducts, {
        keys: [
          {
            name: "product_name",
            weight: 0.8,
          },
          {
            name: "product_desc",
            weight: 0.2,
          },
        ],
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [storeProducts],
  );

  const filteredProducts = useMemo(() => {
    if (!debouncedQuery) return [];

    return fuse
      .search(debouncedQuery, { limit: 10 })
      .map((result) => result.item);
  }, [fuse, debouncedQuery]);

  const handleProductClick = (product: StoreProductModel) => {
    setSearchQuery("");
    navigate(`/menu/${product.cid}/${product.id}`);
  };

  return (
    <div ref={containerRef} className="input-group home-search">
      <input
        id="searchbar"
        type="search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search for dishes, cuisines..."
        className="form-control border-0 shadow-none bg-transparent p-0"
      />

      {!searchQuery && <Search size={20} className="cursor-pointer" />}

      {!!debouncedQuery && (
        <div className="search-dropdown">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product: StoreProductModel) => (
              <div
                key={`product-${product.id}`}
                className="search-dropdown-item"
                onClick={() => handleProductClick(product)}
              >
                <div className="search-product-image-wrapper">
                  <img
                    src={product.product_image || DEFAULT_IMAGE}
                    alt={product.product_name}
                    className={`search-product-image ${
                      !product.product_image ? "p-2" : ""
                    }`}
                  />
                </div>

                <div className="search-dropdown-content">
                  <span className="search-product-name">
                    {product.product_name}
                  </span>

                  <small className="search-category-name">
                    {categoryMap.get(product.cid)}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <div className="search-dropdown-empty">No products found.</div>
          )}
        </div>
      )}
    </div>
  );
};
